import type { FallbackRender } from "@sentry/react";
import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { reloadPage } from "@/infrastructure/navigation";
import { cn } from "@/utils";

/**
 * Fallback UI shown when an unhandled error occurs in React tree
 * Used by Sentry ErrorBoundary
 */
export const ErrorFallback: FallbackRender = ({ error, resetError }) => {
	const navigate = useNavigate();

	const handleGoHome = () => {
		resetError();
		navigate("/");
	};

	const handleReload = () => {
		reloadPage();
	};

	// Extract error message from unknown error type
	const errorMessage =
		error instanceof Error
			? error.message
			: typeof error === "string"
				? error
				: "An unknown error occurred";

	return (
		<div
			className={cn(
				"min-h-screen flex items-center justify-center bg-bg-secondary px-4",
			)}
		>
			<div className="max-w-md w-full bg-bg-primary rounded-lg shadow-sm p-8">
				<div className="flex items-center justify-center w-16 h-16 mx-auto bg-danger-light rounded-full mb-6">
					<AlertCircle className="w-8 h-8 text-danger" />
				</div>

				<h1 className="text-2xl font-bold text-text-primary text-center mb-4">
					Something went wrong
				</h1>

				<p className="text-text-secondary text-center mb-6">
					We&apos;ve been notified and are working on it. Please try refreshing
					the page or go back to the home page.
				</p>

				{import.meta.env.DEV && errorMessage && (
					<div className="mb-6 p-4 bg-bg-tertiary rounded-lg overflow-auto max-h-32">
						<p className="text-xs font-mono text-text-primary break-words">
							{errorMessage}
						</p>
					</div>
				)}

				<div className="flex flex-col sm:flex-row gap-3">
					<button
						onClick={handleReload}
						className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-info-hover hover:bg-info-active text-text-inverse rounded-lg transition-colors"
					>
						<RefreshCw className="w-4 h-4" />
						Refresh Page
					</button>
					<button
						onClick={handleGoHome}
						className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-border-secondary hover:bg-border-primary text-text-primary rounded-lg transition-colors"
					>
						<Home className="w-4 h-4" />
						Go Home
					</button>
				</div>
			</div>
		</div>
	);
};
